import { useCallback, useRef } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  saveGeneralPreferencesBatch,
  type IUserPreferenceGeneralListItem,
} from '@user/store/userPreferences/userPreferencesGeneral'

// Serializes writes between these three calendar hooks only
// (useCalendarFilterPreferences, useActivityTypeFilter,
// useCalendarViewPreference), which each already serialize/debounce their
// OWN writes but know nothing about each other. Without this, a debounced
// save from one and one from another could be in flight at the same time,
// racing the backend's read-modify-write for the same preferences document.
//
// Scope, deliberately not solved here: this is a single module-scope queue,
// so it only protects these hooks within one browser tab. It does NOT
// cover the app's other GeneralPreference writers (MapView filters, density,
// page size, list mode, ...) or a second tab -- `UpdateGeneralPreferences(Batch)`
// has no concurrency control of its own, so those paths can still race each
// other. Routing every `saveGeneralPreferences*` call through one shared lock
// (or making the backend update atomic per-key) would close that gap; out of
// scope for this ticket.
let queue: Promise<unknown> = Promise.resolve()

export function withGeneralPreferencesLock<T>(
  run: () => Promise<T>,
): Promise<T> {
  const result = queue.then(run, run)
  queue = result.then(
    () => undefined,
    () => undefined,
  )
  return result
}

/**
 * Shared debounced-write plumbing for useCalendarFilterPreferences and
 * useActivityTypeFilter: at most one save in flight per caller, a newer value
 * arriving mid-request queues instead of firing concurrently (so an older
 * response can never land after a newer one), and every write goes through
 * the lock above. `onError` is the caller's own failure handling (toast vs
 * log-only) -- pass a `useCallback`'d function so `persist`'s identity stays
 * stable.
 */
export function useQueuedPreferenceWrite(
  onError: (err: unknown) => void,
): (rows: IUserPreferenceGeneralListItem[]) => void {
  const dispatch = useAppDispatch()
  const inFlight = useRef(false)
  const queuedRef = useRef<IUserPreferenceGeneralListItem[] | null>(null)

  const persist = useCallback(
    (rows: IUserPreferenceGeneralListItem[]) => {
      if (inFlight.current) {
        queuedRef.current = rows
        return
      }
      inFlight.current = true

      withGeneralPreferencesLock(() =>
        dispatch(saveGeneralPreferencesBatch(rows)).unwrap(),
      )
        .catch(onError)
        .finally(() => {
          inFlight.current = false
          const queued = queuedRef.current
          if (queued) {
            queuedRef.current = null
            persist(queued)
          }
        })
    },
    [dispatch, onError],
  )

  return persist
}
